import { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  useDispatch,
  useFileCollection,
  useFileCollectionsFromParentId,
} from '@witmetrics/data-store';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { fetchCollectionContents } from '@/utils/files';
import { useSockets } from '@/providers/SocketsProvider';
import { useId } from '@/hooks/useId';
import { useArray } from '@/hooks/useArray';
import SlideTransition from '@/components/SlideTransition';
import type { FileCollectionPath } from '@/components/FileCollectionCrumbs';
import ContentList from '../ContentList';
import { classes, moveCollection } from '../utils';

export type MoveFileCollectionDialogProps = {
  fileCollectionId: number;
  onClose: () => void;
};

export default function MoveFileCollectionDialog({
  fileCollectionId,
  onClose,
}: MoveFileCollectionDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const fileCollection = useFileCollection(fileCollectionId);
  const currentCollection = useFileCollection(
    fileCollection?.parentFileCollectionId
  );
  const { getSocket } = useSockets();
  const [collectionId, setCollectionId] = useId(currentCollection?.id);
  const fileCollections = useFileCollectionsFromParentId(collectionId);
  const [crumbs, setCrumbs] = useArray<FileCollectionPath>();
  const [isFetchingData, toggleFetchingData] = useToggle(true);
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    fetchData();
  }, [collectionId]);

  const fetchData = async () => {
    try {
      if (!collectionId) {
        toggleFetchingData(false);
        showErrorToaster('Error fetching collection contents');
        return;
      }
      // TODO: pagination
      toggleFetchingData(true);
      const data = await fetchCollectionContents(collectionId);
      dispatch.addFileCollections(data.fileCollections.results);
      setCrumbs(data.path);
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching collection contents', () => fetchData());
    }
  };

  const handleSave = async (selectedCollectionId?: number) => {
    // Specify a child collection or use the current collectionId from state
    try {
      toggleSaving(true);
      const result = await moveCollection(
        fileCollectionId,
        selectedCollectionId || collectionId!
      );
      // Dispatch only if the socket isn't handling it
      if (!getSocket()?.socket.connected) {
        dispatch.updateFileCollection(result);
      }
      showSuccessToaster('Collection moved');
      toggleSaving(false);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error moving collection', () => {
        handleSave();
      });
    }
  };

  // NOTE: Exclude the collection itself and its current parent
  const filtered = fileCollections.filter(
    (fc) =>
      fc.id !== fileCollectionId &&
      fc.id !== fileCollection?.parentFileCollectionId
  );
  return (
    <Dialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
      TransitionComponent={SlideTransition}
      onClose={onClose}>
      <ContentList
        isFetchingData={isFetchingData}
        isSaving={isSaving}
        disableMoveToCurrent={collectionId === currentCollection?.id}
        crumbs={crumbs}
        fileCollections={filtered}
        onSelect={setCollectionId}
        onSave={handleSave}
        onClose={onClose}
      />
    </Dialog>
  );
}
